/**
 * Maps a digital wallet's own card-network vocabulary onto the brand string
 * Adyen's Card component reports via `onBrand` (e.g. `visa`, `mc`, `amex`).
 * Shared by `useApplePay` and (future) `useGooglePay` — both source `brand`
 * from their wallet's authorized event rather than the Card form, and both
 * must land on the same vocabulary the internal API already expects.
 * Unrecognized networks pass through lower-cased and space-stripped —
 * Adyen's `sendAdyenSessionResult` owns validating the value, mirroring how
 * unknown Adyen result codes are forwarded raw elsewhere in this hook.
 */
const KNOWN_WALLET_NETWORKS: Record<string, string> = {
  visa: 'visa',
  mastercard: 'mc',
  amex: 'amex',
  americanexpress: 'amex',
  discover: 'discover',
  jcb: 'jcb',
  maestro: 'maestro',
  elo: 'elo',
  interac: 'interac',
  chinaunionpay: 'cup',
  eftpos: 'eftpos',
}

/**
 * Normalizes a wallet-reported network string (Apple Pay's `network`, Google
 * Pay's `cardNetwork`) into the brand vocabulary Adyen's Card component uses.
 */
export function normalizeWalletBrand(network: string): string {
  const key = network.toLowerCase().replace(/\s+/g, '')
  return KNOWN_WALLET_NETWORKS[key] ?? key
}

/**
 * Extracts the last four digits from a wallet's display string (Apple Pay's
 * `displayName`, e.g. `"Visa 1234"`). Anchored to the trailing digit run —
 * a card network name that happened to contain four digits would otherwise
 * win over the actual last-4. Returns `undefined` when the string does not
 * end in a run of exactly four digits (optionally followed by non-digits).
 */
export function extractEndDigits(displayName: string): string | undefined {
  return displayName.match(/(\d{4})\D*$/)?.[1]
}
